// DGN, the gas coin: per-action fees, the daily check-in streak, and the WUSD <-> DGN swap on Pumpr.

import { TUNABLES } from "@degen/shared/config/tunables";
import { CURRENCY } from "@degen/shared/config/content";
import type { Command } from "./engine";
import { dayOf, pushLog, type GameState } from "./state";
import { gas, money } from "@degen/shared/money";

const G = TUNABLES.gas;

export interface GasState {
  checkInDay: number; // last day checked in (-1 = never)
  streak: number;
  spent: number; // lifetime DGN burned on fees
}

export function createGasState(): GasState {
  return { checkInDay: -1, streak: 0, spent: 0 };
}

/** DGN this command burns (0 = free). */
export function gasFee(cmd: Command): number {
  if (cmd.type === "FARM_TASK") return G.farmFees[cmd.task] ?? 0;
  if (cmd.type === "SWAP_DGN") return cmd.side === "sell" ? G.fees.SWAP_DGN_SELL : 0;
  if (cmd.type === "JOIN_HACKATHON") return TUNABLES.hackathons.entryDgn;
  if (cmd.type === "APPLY_FILL") return G.fees[cmd.side === "buy" ? "BUY_TOKEN" : "SELL_TOKEN"];
  if (cmd.type === "APPLY_LAUNCH") return G.fees.LAUNCH_COIN;
  return G.fees[cmd.type] ?? 0;
}

export function outOfGasMessage(fee: number, have: number): string {
  return `Out of gas ⛽ This needs ${gas(fee)} and you have ${gas(have)}. Check in for free ${CURRENCY.gas.code}, finish quests, or swap on Pumpr.`;
}

export function checkInReward(streak: number): number {
  return G.checkIn.base + G.checkIn.perStreakDay * (Math.min(streak, G.checkIn.maxStreak) - 1);
}

export function canCheckIn(state: GameState): boolean {
  return state.player.gas.checkInDay < dayOf(state.tick);
}

/** Once per in-game day. Consecutive days build a streak (bigger drip). */
export function dailyCheckIn(state: GameState): { ok: true; data: { dgn: number; streak: number } } | { ok: false; error: string } {
  const p = state.player;
  const day = dayOf(state.tick);
  if (p.gas.checkInDay >= day) return { ok: false, error: "Already checked in today. Come back tomorrow." };
  p.gas.streak = p.gas.checkInDay === day - 1 ? p.gas.streak + 1 : 1;
  p.gas.checkInDay = day;
  const dgn = checkInReward(p.gas.streak);
  p.dgn += dgn;
  pushLog(state, "checkin", `⛽ Daily check-in: +${gas(dgn)} (streak ${p.gas.streak}).`);
  return { ok: true, data: { dgn, streak: p.gas.streak } };
}

/** Quote a swap. buy: spend WUSD to get `amount` DGN. sell: sell `amount` DGN for WUSD. */
export function quoteSwap(side: "buy" | "sell", amount: number): number {
  return side === "buy" ? amount * G.priceWusd * (1 + G.swapSpread) : amount * G.priceWusd * (1 - G.swapSpread);
}

export function swapDgn(state: GameState, side: "buy" | "sell", amount: number): { ok: true; data: { dgn: number; wusd: number } } | { ok: false; error: string } {
  const p = state.player;
  if (!(amount >= G.minSwapDgn) || !Number.isFinite(amount)) return { ok: false, error: `Minimum swap is ${gas(G.minSwapDgn)}.` };
  const wusd = quoteSwap(side, amount);
  if (side === "buy") {
    if (p.cash + 1e-9 < wusd) return { ok: false, error: `That costs ${money(wusd)}. Not enough ${CURRENCY.main.code}.` };
    p.cash = Math.max(0, p.cash - wusd);
    p.dgn += amount;
  } else {
    if (p.dgn + 1e-9 < amount + G.fees.SWAP_DGN_SELL) return { ok: false, error: `You only have ${gas(p.dgn)} (and selling burns ${gas(G.fees.SWAP_DGN_SELL)}).` };
    p.dgn -= amount;
    p.cash += wusd;
  }
  return { ok: true, data: { dgn: amount, wusd } };
}

export function dgnValue(state: GameState): number {
  return state.player.dgn * G.priceWusd;
}
